/**
 * BookingDetailUI.jsx
 * Module: common / Booking Detail
 *
 * Pure field resolvers and formatting helpers for booking payloads — safeNum,
 * formatAWB, getFlag, resolveStoredCommodity, resolveTemperatureRange,
 * resolveProductCommodityDetails and friends. The fields they read are spread
 * across differently-shaped copies of the search query depending on a
 * booking's age and source, so every surface that needs them probes the same
 * way instead of each re-deriving it.
 *
 * Consumers: pages/BookingDetailPage.jsx (air) and
 * components/BookingHistory/OceanCargoDetails.jsx (ocean), plus the checkout
 * and manual-quote screens.
 *
 * No rendering lives here any more. The Tailwind primitives this module used
 * to export (SectionCard, InfoCell, StatCard, StatusBadge, the commodity /
 * temperature / product chips and CommodityDetailsSection) went unused when
 * both detail pages moved to the MUI design in BookingDetailMuiUI.jsx, and
 * have been removed. CHARGE_COLORS is the one styling export left — the
 * checkout breakdown still cycles it.
 *
 * Author: FreightSee frontend team
 * Date: 2026-07-06
 */
import moment from "moment";

/**
 * Coerces a value to a finite number, or null when it isn't one.
 * Used throughout the charge-breakdown math so a missing/garbage field
 * (undefined, "", "N/A") safely falls out of a sum instead of producing NaN.
 *
 * @param {*} v - raw value from the API (string, number, null, undefined…)
 * @returns {number|null} the numeric value, or null if it can't be parsed
 */
export const safeNum = (v) => (v != null && !isNaN(Number(v)) ? Number(v) : null);

/**
 * Parses a flight-segment date/time that may arrive in either the legacy
 * "DD-MMM-YYYY" shape or full ISO (e.g. "2026-08-07T22:50") depending on the
 * data source (manual quote vs. live FS response). Passing both formats lets
 * moment pick whichever one actually matches instead of always assuming
 * "DD-MMM-YYYY" and silently producing "Invalid date" for ISO strings.
 *
 * @param {string} value - raw date/time string from the API
 * @returns {import('moment').Moment} parsed moment instance
 */
export const parseSegDate = (value) => moment(value, ["DD-MMM-YYYY", moment.ISO_8601]);

/**
 * Formats an AWB (Air Waybill) number with its 3-digit airline prefix.
 *
 * Why: the backend stores the AWB number and the airline prefix as separate
 * fields (e.g. num="79839325", prefix="014"), but the industry-standard way
 * to display an AWB is "<prefix>-<number>" (e.g. "014-79839325"). Some older
 * records already contain the prefix baked into the number itself — the
 * `str.length > 8` guard treats those as already-formatted and leaves them
 * alone rather than double-prefixing them.
 *
 * @param {string|number} num - the AWB number, with or without prefix
 * @param {string|number} [prefix] - the 3-digit airline prefix, if known
 * @returns {string} the formatted AWB (e.g. "014-79839325"), or "" if num is empty/invalid
 */
export const formatAWB = (num, prefix) => {
  if (!num) return '';
  const str = String(num).trim();
  if (!str || str === 'undefined' || str === 'null') return '';
  if (!prefix || str.length > 8) return str;
  return `${prefix}-${str}`;
};

/**
 * Builds the display AWB ("<prefix>-<number>", e.g. "157-97701682") for a row
 * from the air booking-history list. Both halves live in different places
 * depending on how old the booking is: the number is either top-level or
 * inside bookeditem, and the prefix is either the row's own airlineprefix or
 * the first flight itinerary's airlinePrefix — so probe all of them before
 * giving up. `bookeditem` arrives as a JSON string on some rows and as an
 * already-parsed object on others, hence the try/catch.
 *
 * Lives here so the desktop table (AirCargoList), the mobile card list
 * (AirMobBookedList) and the Excel export all resolve it the same way.
 *
 * @param {Object} record - booking row from the air booking-history list
 * @returns {string} formatted AWB, or "" when the booking has no AWB yet
 */
export const resolveBookingAwb = (record) => {
  let bookeditem = record?.bookeditem;
  if (typeof bookeditem === 'string') {
    try {
      bookeditem = JSON.parse(bookeditem);
    } catch (e) {
      bookeditem = {};
    }
  }
  bookeditem = bookeditem || {};
  const awb = record?.airWaybillNumber || bookeditem?.airWaybillNumber;
  const prefix =
    record?.airlineprefix ||
    record?.airlinePrefix ||
    bookeditem?.flightItineraries?.[0]?.airlinePrefix;
  return formatAWB(awb, prefix);
};

/**
 * Derives a per-kg rate for a single charge line when the backend didn't
 * supply one directly (older chargesBreakDown records only carry the total
 * amount, not the rate). Divides the charge's total amount by the shipment's
 * chargeable weight so the UI can still show a "₹X /kg" hint under the charge.
 *
 * @param {number} chargeAmount - the charge's total amount
 * @param {number} chargeableWt - the shipment's chargeable weight (kg)
 * @returns {number|null} the derived per-kg rate, or null if either input is missing/zero
 */
export const calcPerKg = (chargeAmount, chargeableWt) => {
  const amt = safeNum(chargeAmount);
  const wt = safeNum(chargeableWt);
  if (!amt || !wt || wt === 0) return null;
  return amt / wt;
};

// IATA airport code → country flag emoji, used next to the origin/destination
// codes in the route banner. Not exhaustive — only the lanes this app quotes.
const FLAGS = {
  DEL: '🇮🇳', BOM: '🇮🇳', BLR: '🇮🇳', MAA: '🇮🇳', CCU: '🇮🇳', HYD: '🇮🇳', AMD: '🇮🇳', COK: '🇮🇳',
  JFK: '🇺🇸', LAX: '🇺🇸', ORD: '🇺🇸', SFO: '🇺🇸', MIA: '🇺🇸', DFW: '🇺🇸', ATL: '🇺🇸', EWR: '🇺🇸',
  LHR: '🇬🇧', LGW: '🇬🇧', MAN: '🇬🇧',
  DXB: '🇦🇪', AUH: '🇦🇪', SHJ: '🇦🇪',
  DOH: '🇶🇦', BAH: '🇧🇭', KWI: '🇰🇼', RUH: '🇸🇦', JED: '🇸🇦',
  SIN: '🇸🇬', KUL: '🇲🇾', BKK: '🇹🇭', HKG: '🇭🇰', CGK: '🇮🇩',
  NRT: '🇯🇵', HND: '🇯🇵', ICN: '🇰🇷', PEK: '🇨🇳', PVG: '🇨🇳',
  SYD: '🇦🇺', MEL: '🇦🇺',
  CDG: '🇫🇷', FRA: '🇩🇪', MUC: '🇩🇪', AMS: '🇳🇱', ZRH: '🇨🇭', BCN: '🇪🇸', FCO: '🇮🇹',
  YYZ: '🇨🇦', YVR: '🇨🇦', YUL: '🇨🇦',
  GRU: '🇧🇷', EZE: '🇦🇷',
  JNB: '🇿🇦', CAI: '🇪🇬', NBO: '🇰🇪',
  IST: '🇹🇷', TLV: '🇮🇱',
};

/**
 * Looks up the flag emoji for an IATA airport code.
 * @param {string} iata - 3-letter IATA airport code (case-insensitive)
 * @returns {string} the matching flag emoji, or a generic globe if unknown
 */
export const getFlag = (iata) => FLAGS[(iata || '').toUpperCase()] || '🌍';

/**
 * Derives a flag emoji from an ISO 3166-1 alpha-2 country code by composing
 * the two Unicode regional-indicator symbols (e.g. "IN" -> 🇮🇳). Used for
 * ocean UN/LOCODEs (e.g. "INMUN"), whose first two letters are already the
 * country code, so — unlike `getFlag` — no per-code lookup table is needed.
 * @param {string} code - a UN/LOCODE or any string starting with a 2-letter country code
 * @returns {string} the matching flag emoji, or a generic globe if invalid
 */
export const getFlagByCountryCode = (code) => {
  const cc = (code || '').slice(0, 2).toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '🌍';
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + (c.charCodeAt(0) - 65)));
};

// Colour palette cycled across charge-breakdown groups (Main, Fuel, Security,
// GST, …) so each category gets a visually distinct left-border/header colour
// without hardcoding a colour per category name — new categories just pick up
// the next colour in rotation.
export const CHARGE_COLORS = [
  { border: 'border-blue-400',   headerBg: 'bg-blue-50',   nameText: 'text-blue-800',   totalText: 'text-blue-600'   },
  { border: 'border-green-400',  headerBg: 'bg-green-50',  nameText: 'text-green-800',  totalText: 'text-green-600'  },
  { border: 'border-gray-400',   headerBg: 'bg-gray-50',   nameText: 'text-gray-700',   totalText: 'text-gray-700'   },
  { border: 'border-teal-400',   headerBg: 'bg-teal-50',   nameText: 'text-teal-800',   totalText: 'text-teal-600'   },
  { border: 'border-violet-400', headerBg: 'bg-violet-50', nameText: 'text-violet-800', totalText: 'text-violet-600' },
];

// ─── Commodity / temperature resolution ─────────────────────────────────────
// Both live here (rather than in one page) because every booking-detail top
// band renders the same commodity + temperature chips, and the fields they read
// are spread across differently-shaped copies of the search query depending on
// the booking's age and source.

/** Strings that mean "no value" once they've been round-tripped through the DB. */
const BLANK_TEXTS = ['null', 'undefined', 'nan', 'n/a', 'na', 'none', 'false', '-', '--'];

/**
 * Normalises anything the payload might hold for a text field into a clean
 * display string, or '' when there's nothing usable.
 *
 * Handles plain strings, numbers, `{ commodity_description | label | name |
 * value }` objects (the commodity dropdown option shape used by the search
 * forms), arrays of either, and the string sentinels older bookings carry
 * because the field was stringified before saving ("null", "N/A", …).
 *
 * @param {*} v - raw value from the booking payload
 * @returns {string} trimmed display text, or '' when not meaningful
 */
const cleanText = (v) => {
  if (v == null || v === false) return '';
  if (Array.isArray(v)) {
    for (const item of v) {
      const t = cleanText(item);
      if (t) return t;
    }
    return '';
  }
  if (typeof v === 'object') {
    return cleanText(
      // commonditytype covers the nested { commonditytype: "…" } shape the
      // manual-quote payloads use (and the search forms' misspelling).
      v.commodity_description ?? v.commodityDescription ?? v.commonditytype ??
      v.tempraturerange ?? v.temperatureRange ?? v.label ?? v.name ?? v.value
    );
  }
  if (typeof v !== 'string' && typeof v !== 'number') return '';
  // Collapse the newlines / double spaces some descriptions carry over from the
  // commodity CSV import so the chip stays on a single line.
  const s = String(v).replace(/\s+/g, ' ').trim();
  if (!s) return '';
  return BLANK_TEXTS.includes(s.toLowerCase()) ? '' : s;
};

/**
 * Every object on a booking that can carry search-query fields. single_booking_data
 * returns `search_query_data` or `searchquery` depending on the booking's age, the
 * query fields sometimes sit on the root, and airline (QR) bookings mirror them
 * under airlinepayloadData — so all of them are probed in priority order.
 *
 * @param {object|null} d - single_booking_data payload
 * @returns {Array<object>} candidate source objects, most-authoritative first
 */
const querySources = (d) => {
  if (!d || typeof d !== 'object') return [];
  return [
    d.search_query_data,
    d.searchquery,
    d,
    d.search_query_data?.airlinepayloadData,
    d.searchquery?.airlinepayloadData,
    d.airlinepayloadData,
    d.bookeditem,
    d.bookeditem?.airlinepayloadData,
  ].filter((o) => o && typeof o === 'object');
};

/**
 * Picks the commodity description stored on the booking itself.
 *
 * Why: the description IS persisted with the booking
 * (search_query_data.goodsDetails.commodity_description, e.g. "Fats and oils
 * (Perishables)"). BookingDetailPage used to ignore it and resolve the
 * name from goodsDetails.commodityId over the commodity-data service instead —
 * a call that 404s for some ids (e.g. 27) and whose QR_code branch deliberately
 * prefers the "GCR" (General Cargo) row when one QR_code maps to several product
 * codes, so both paths silently degraded the real commodity to the "General
 * Cargo" placeholder. The stored value is the booked truth, so it's read
 * directly and no network lookup is involved.
 *
 * @param {object|null} d - single_booking_data payload
 * @returns {string} commodity description, or '' when the booking has none
 */
export const resolveStoredCommodity = (d) => {
  const readers = [
    (q) => q?.goodsDetails?.commodity_description,
    (q) => q?.goodsDetails?.commodityDescription,
    (q) => q?.goodsDetails?.commodity,
    (q) => q?.goodsDetails?.commonditytype,
    (q) => q?.goodsDetails?.cargoType,
    (q) => q?.productDetails?.natureOfGoods,
    (q) => q?.commodity_description,
    (q) => q?.commonditytype,   // legacy misspelling used by the search forms
    (q) => q?.commodity,
  ];
  for (const q of querySources(d)) {
    for (const read of readers) {
      const t = cleanText(read(q));
      if (t) return t;
    }
  }
  return '';
};

/**
 * Formats a stored temperature range for display as "2°C to 8°C" (or "-10°C"
 * for a single value).
 *
 * Values are saved as the bare option value the search form wrote — "2-8",
 * "15-25", "2-25" or "-10" (see the Perishables temperature toggles in
 * Newsearchform/NewsearchForm.jsx) — so the unit is added per bound here.
 * Already-decorated values ("2-8°C", "2-8 C") are re-normalised rather than
 * double-suffixed, either bound may be negative, the two are ordered
 * coldest-first, and anything in an unrecognised shape is shown verbatim
 * instead of being dropped.
 *
 * @param {*} raw - stored temperature range
 * @returns {string} e.g. "2°C to 8°C", or '' when there's no range
 */
export const formatTemperatureRange = (raw) => {
  const s = cleanText(raw);
  if (!s) return '';
  // Drop any unit already present ("2-8°C", "2-8 C", "2-8c", "2-8℃") so it
  // can't be doubled, then re-add it per bound below.
  const bare = s.replace(/\s*(?:[°℃]\s*c?|c)\.?\s*$/i, '').trim();
  // A single value, or two bounds with whichever separator the data uses.
  const m = bare.match(/^(-?\d+(?:\.\d+)?)(?:\s*(?:-|–|to)\s*(-?\d+(?:\.\d+)?))?$/i);
  // Unrecognised shape (e.g. a free-text "Ambient") — show it as-is rather than
  // dropping information the booking actually carries.
  if (!m) return s;
  const [, loRaw, hiRaw] = m;
  if (hiRaw == null || Number(loRaw) === Number(hiRaw)) return `${loRaw}°C`;
  // Coldest first — the frozen option stores its bounds inverted (-10 / -30).
  const ordered = Number(loRaw) <= Number(hiRaw) ? [loRaw, hiRaw] : [hiRaw, loRaw];
  return `${ordered[0]}°C to ${ordered[1]}°C`;
};

/**
 * Resolves the booking's temperature range, falling back to the stored
 * lower/upper bound pair when the range string itself is missing. Bounds are
 * min/max-ordered because the frozen option saves them inverted
 * (lower: -10, upper: -30).
 *
 * @param {object|null} d - single_booking_data payload
 * @returns {string} display-ready range (e.g. "2-8°C"), or '' when none
 */
export const resolveTemperatureRange = (d) => {
  const readers = [
    (q) => q?.temperatureRange,
    (q) => q?.temperature_range,
    (q) => q?.tempraturerange,
    (q) => q?.goodsDetails?.temperatureRange,
    (q) => q?.goodsDetails?.temperature_range,
    (q) => q?.temperaturecontrol?.tempraturerange,
  ];
  for (const q of querySources(d)) {
    for (const read of readers) {
      const t = formatTemperatureRange(read(q));
      if (t) return t;
    }
    // Clearing the range writes "" to both bounds, and safeNum("") is 0 — so
    // reject blanks first, and treat an all-zero pair as "no range" too, rather
    // than painting a bogus "0°C" chip on an ambient shipment.
    const strictNum = (v) =>
      typeof v === 'number' || (typeof v === 'string' && v.trim() !== '') ? safeNum(v) : null;
    const lo = strictNum(q?.lowertempbound ?? q?.lowerbound ?? q?.goodsDetails?.lowertempbound);
    const hi = strictNum(q?.uppertempbound ?? q?.upperbound ?? q?.goodsDetails?.uppertempbound);
    if (lo != null && hi != null && !(lo === 0 && hi === 0)) {
      return lo === hi ? `${lo}°C` : `${Math.min(lo, hi)}°C to ${Math.max(lo, hi)}°C`;
    }
  }
  return '';
};

/**
 * Special-handling codes that `withTemperatureHandlingCode` in
 * src/utils/buildRateSearchPayload.js appends to the search payload purely as a
 * translation of the selected temperature range. They're excluded from the SHC
 * list the booking-detail surfaces render, since those surfaces show the
 * temperature range on its own already.
 */
const TEMPERATURE_DERIVED_SHC = new Set(['COL', 'CRT', 'ERT']);

/**
 * Resolves the carrier's product/commodity block for a booking — the
 * `productCommodityDetails` object the rate socket returned for the selected
 * option and that NewBookingFlowContent.jsx persists onto bookingData
 * (productCode / commodityCode / natureOfGoods / densityGroup / dgShipment /
 * serviceCode / specialHandlingCodes).
 *
 * It's probed across several shapes because only bookings made through the
 * current flow carry it on `bookeditem`; QR/airline bookings mirror the same
 * fields under `airlinepayloadData.productDetails`, and older/manual bookings
 * have nothing but the search query's own `goodsDetails.specialHandlingCodes`
 * — which is used as the SHC fallback so those bookings still show their
 * handling codes instead of an empty row.
 *
 * @param {object|null} d - single_booking_data payload
 * @returns {{productCode:string, commodityCode:string, natureOfGoods:string,
 *   densityGroup:string, serviceCode:string, specialHandlingCodes:string[],
 *   dgShipment:boolean}|null} normalised block, or null when the booking
 *   carries none of these fields at all
 */
export const resolveProductCommodityDetails = (d) => {
  if (!d || typeof d !== 'object') return null;

  // Most authoritative first: what was actually booked, then the airline
  // payload, then the search query's product block.
  const candidates = [
    d.bookeditem?.productCommodityDetails,
    d.productCommodityDetails,
    d.bookeditem?.airlinepayloadData?.productDetails,
    d.airlinepayloadData?.productDetails,
    d.search_query_data?.airlinepayloadData?.productDetails,
    d.searchquery?.airlinepayloadData?.productDetails,
    d.search_query_data?.productDetails,
    d.searchquery?.productDetails,
  ].filter((o) => o && typeof o === 'object' && !Array.isArray(o));

  const pick = (...keys) => {
    for (const c of candidates) {
      for (const k of keys) {
        const t = cleanText(c[k]);
        if (t) return t;
      }
    }
    return '';
  };

  // First non-empty code list; falls back to the search query's goodsDetails
  // for bookings whose rate option had no productCommodityDetails.
  const shcSources = [
    ...candidates.map((c) => c.specialHandlingCodes ?? c.specialHandlingCode),
    ...querySources(d).map((q) => q?.goodsDetails?.specialHandlingCodes),
  ];
  let specialHandlingCodes = [];
  for (const raw of shcSources) {
    const list = (Array.isArray(raw) ? raw : [raw])
      .map((c) => cleanText(c).toUpperCase())
      .filter(Boolean)
      // Drop the codes buildRateSearchPayload derives from temperatureRange
      // (2-8 -> COL, 15-25 -> CRT, 2-25 -> ERT). They aren't shipper-selected
      // handling codes, and both surfaces already render the range itself —
      // the temperature chip in the banner and the "Temperature Range" cell in
      // CommodityDetailsSection — so listing them again just says "2ºC to 8ºC,
      // COL". PER is deliberately kept: it comes from the commodity, not the
      // temperature widget.
      .filter((c) => !TEMPERATURE_DERIVED_SHC.has(c));
    // De-duplicate — QR options repeat a code across product/goods blocks.
    const unique = [...new Set(list)];
    if (unique.length) { specialHandlingCodes = unique; break; }
  }

  const dgShipment = candidates.some((c) => c.dgShipment === true || c.dgShipment === 'true');

  const details = {
    productCode:    pick('productCode'),
    commodityCode:  pick('commodityCode'),
    natureOfGoods:  pick('natureOfGoods'),
    densityGroup:   pick('densityGroup'),
    serviceCode:    pick('serviceCode'),
    specialHandlingCodes,
    dgShipment,
  };

  const hasAny =
    details.productCode || details.commodityCode || details.natureOfGoods ||
    details.densityGroup || details.serviceCode || specialHandlingCodes.length || dgShipment;
  return hasAny ? details : null;
};
